// @mui
import {
  Box,
  Button,
  Drawer,
  IconButton,
  InputBase,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from "@mui/material"
// components
import Iconify from "./Iconify"
import Scrollbar from "./scrollbar"
import Calender from "../mui/Calendar"
import SearchHome from "../mui/SearchBar"

const drawerWidth = 424

const sectionSx = {
  backgroundColor: "#fff",
  border: "1px solid #E5E7EB",
  borderRadius: "10px",
  boxShadow: "0 8px 22px rgba(15, 23, 42, 0.04)",
  p: 2.25
}

const toggleSx = {
  display: "grid",
  width: "100%",
  "& .MuiToggleButton-root": {
    borderColor: "#D8DDE6",
    color: "#6B7280",
    fontSize: "0.78rem",
    fontWeight: 700,
    letterSpacing: 0,
    minHeight: 42,
    px: 1.5,
    textTransform: "uppercase",
    whiteSpace: "nowrap"
  },
  "& .Mui-selected": {
    backgroundColor: "#EEF5FF !important",
    color: "#1976D2 !important"
  }
}

function FilterButton({ onClick }) {
  return (
    <Button
      disableRipple
      color="inherit"
      endIcon={<Iconify icon="ic:round-filter-list" />}
      onClick={onClick}
      sx={{
        color: "#3E4755",
        fontWeight: 700,
        letterSpacing: 0,
        textTransform: "uppercase"
      }}
    >
      Filters
    </Button>
  )
}

function FilterSection({ title, children }) {
  return (
    <Box sx={sectionSx}>
      <Typography
        variant="subtitle2"
        sx={{ color: "#111827", fontSize: "0.95rem", fontWeight: 800, mb: 1.5 }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  )
}

function FilterDrawerShell({ openFilter, onOpenFilter, onCloseFilter, onReset, fetchData, children }) {
  return (
    <>
      <FilterButton onClick={onOpenFilter} />

      <Drawer
        anchor="left"
        open={openFilter}
        onClose={onCloseFilter}
        PaperProps={{
          sx: {
            backgroundColor: "#F8FAFC",
            border: "none",
            display: "flex",
            flexDirection: "column",
            height: "100dvh",
            maxWidth: "100vw",
            overflow: "hidden",
            width: { xs: "100vw", sm: drawerWidth }
          }
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            backgroundColor: "#fff",
            borderBottom: "1px solid #E5E7EB",
            px: 2.5,
            py: 2.25
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ color: "#111827", fontSize: "1.1rem", fontWeight: 800 }}>
              Filters
            </Typography>
            <Typography sx={{ color: "#6B7280", fontSize: "0.78rem" }}>
              Refine the records shown on this page
            </Typography>
          </Box>
          <IconButton
            onClick={onCloseFilter}
            sx={{
              backgroundColor: "#F3F4F6",
              color: "#4B5563",
              "&:hover": { backgroundColor: "#E5E7EB" }
            }}
          >
            <Iconify icon="eva:close-fill" />
          </IconButton>
        </Stack>

        <Scrollbar sx={{ flex: "1 1 auto", minHeight: 0, overflowX: "hidden" }}>
          <Stack spacing={2.25} sx={{ p: 2.5 }}>
            {children}
          </Stack>
        </Scrollbar>

        <Box
          sx={{
            backgroundColor: "#fff",
            borderTop: "1px solid #E5E7EB",
            display: "grid",
            gap: 1.25,
            gridTemplateColumns: "1fr 1.4fr",
            p: 2.5
          }}
        >
          <Button
            onClick={onReset}
            fullWidth
            variant="outlined"
            startIcon={<Iconify icon="ic:round-clear-all" />}
            sx={{
              borderColor: "#D8DDE6",
              borderRadius: "999px",
              color: "#3E4755",
              fontWeight: 800,
              minHeight: 42
            }}
          >
            Clear
          </Button>
          <Button
            onClick={() => fetchData()}
            fullWidth
            variant="contained"
            className="btn_bg_secondery"
            sx={{
              borderRadius: "999px",
              boxShadow: "0 8px 18px rgba(62, 71, 85, 0.24)",
              fontWeight: 800,
              minHeight: 42
            }}
          >
            Apply Filters
          </Button>
        </Box>
      </Drawer>
    </>
  )
}

function SearchBlock({ values, handleChange, data }) {
  return (
    <FilterSection title="Search">
      <SearchHome
        data={data}
        search={values.search}
        setSearch={value => handleChange("search", value)}
        setKeyword={value => handleChange("keyword", value)}
        keyword={values.keyword}
        style={{ width: "100%" }}
      />
    </FilterSection>
  )
}

function DateFilterBlock({ values, handleChange, date, setDate, includeReport = false }) {
  return (
    <FilterSection title="Date Filter Type">
      <ToggleButtonGroup
        color="primary"
        value={values.date_search_type}
        onChange={(e, value) => value && handleChange("date_search_type", value)}
        exclusive
        aria-label="Date Filter"
        sx={{
          ...toggleSx,
          gridTemplateColumns: includeReport ? "repeat(3, 1fr)" : "repeat(2, 1fr)",
          mb: 1.5
        }}
      >
        <ToggleButton value="client">Client</ToggleButton>
        <ToggleButton value="sample">Sample</ToggleButton>
        {includeReport && <ToggleButton value="report">Report</ToggleButton>}
      </ToggleButtonGroup>
      <Calender date={date} setDate={setDate} />
    </FilterSection>
  )
}

export default function ShopFilterSidebar({
  openFilter,
  onOpenFilter,
  onCloseFilter,
  handleChange,
  values,
  fetchData,
  onReset,
  date,
  setDate
}) {
  return (
    <FilterDrawerShell
      openFilter={openFilter}
      onOpenFilter={onOpenFilter}
      onCloseFilter={onCloseFilter}
      fetchData={fetchData}
      onReset={onReset}
    >
      <SearchBlock
        values={values}
        handleChange={handleChange}
        data={[
          { route: "barcode", name: "Barcode" },
          { route: "name", name: "Client" },
          { route: "email", name: "Email" },
          { route: "pet_name", name: "Pet Name" }
        ]}
      />
      <FilterSection title="Archive Reports">
        <Box sx={{ alignItems: "center", display: "flex", justifyContent: "space-between" }}>
          <Typography sx={{ color: "#4B5563", fontSize: "0.9rem" }}>Include archived reports</Typography>
          <Switch
            checked={values.archive}
            onChange={e => handleChange("archive", e.target.checked)}
          />
        </Box>
      </FilterSection>
      <DateFilterBlock
        values={values}
        handleChange={handleChange}
        date={date}
        setDate={setDate}
        includeReport
      />
    </FilterDrawerShell>
  )
}

export function SampleShopFilterSidebar({
  openFilter,
  onOpenFilter,
  onCloseFilter,
  handleChange,
  values,
  fetchData,
  onReset,
  date,
  setDate
}) {
  return (
    <FilterDrawerShell
      openFilter={openFilter}
      onOpenFilter={onOpenFilter}
      onCloseFilter={onCloseFilter}
      fetchData={fetchData}
      onReset={onReset}
    >
      <SearchBlock
        values={values}
        handleChange={handleChange}
        data={[
          { route: "barcode", name: "Barcode" },
          { route: "name", name: "Client" },
          { route: "pet_name", name: "Pet Name" }
        ]}
      />
      <DateFilterBlock values={values} handleChange={handleChange} date={date} setDate={setDate} />
    </FilterDrawerShell>
  )
}

export function ClientShopFilterSidebar({
  openFilter,
  onOpenFilter,
  onCloseFilter,
  handleChange,
  values,
  fetchData,
  onReset,
  date,
  setDate
}) {
  return (
    <FilterDrawerShell
      openFilter={openFilter}
      onOpenFilter={onOpenFilter}
      onCloseFilter={onCloseFilter}
      fetchData={fetchData}
      onReset={onReset}
    >
      <SearchBlock
        values={values}
        handleChange={handleChange}
        data={[
          { route: "barcode", name: "Barcode" },
          { route: "name", name: "Client" },
          { route: "pet_name", name: "Pet Name" }
        ]}
      />
      <FilterSection title="Client Register Date">
        <Calender date={date} setDate={setDate} />
      </FilterSection>
    </FilterDrawerShell>
  )
}

export function BarcodeFilterSidebar({
  openFilter,
  onOpenFilter,
  onCloseFilter,
  handleChange,
  values,
  fetchData,
  onReset
}) {
  return (
    <FilterDrawerShell
      openFilter={openFilter}
      onOpenFilter={onOpenFilter}
      onCloseFilter={onCloseFilter}
      fetchData={fetchData}
      onReset={onReset}
    >
      <FilterSection title="Search">
        <Box
          sx={{
            alignItems: "center",
            backgroundColor: "#F8FAFC",
            border: "1px solid #D8DDE6",
            borderRadius: "12px",
            display: "flex",
            minHeight: 46,
            px: 1.5
          }}
        >
          <InputBase
            fullWidth
            placeholder="Search by barcode..."
            type="search"
            value={values.barcode}
            onChange={e => handleChange("barcode", e.target.value)}
            sx={{
              color: "#111827",
              fontSize: "0.95rem",
              "& input::placeholder": { color: "#8A94A6", opacity: 1 }
            }}
          />
        </Box>
      </FilterSection>
      <FilterSection title="Status">
        <ToggleButtonGroup
          color="primary"
          value={values.status}
          onChange={(e, value) => handleChange("status", value || "")}
          exclusive
          aria-label="Status"
          sx={{
            ...toggleSx,
            gridTemplateColumns: "repeat(2, 1fr)",
            "& .MuiToggleButton-root": {
              ...toggleSx["& .MuiToggleButton-root"],
              minHeight: 40
            }
          }}
        >
          <ToggleButton value="">All</ToggleButton>
          <ToggleButton value="activated">Activated</ToggleButton>
          <ToggleButton value="not-activated">Not Activated</ToggleButton>
          <ToggleButton value="disabled">Disabled</ToggleButton>
        </ToggleButtonGroup>
      </FilterSection>
    </FilterDrawerShell>
  )
}
